import { Controller } from "@hotwired/stimulus"

// Popover fuer gekuerzte Textfelder (truncate_hover-Helper):
// Hover/Klick auf den gekuerzten Text oeffnet ein Tailwind-Popover mit dem
// VOLLSTAENDIGEN Text und einem Kopieren-Button. Bleibt offen, solange die
// Maus ueber Trigger oder Popover ist; schliesst bei ESC/Outside-Click.
//
// Das Popover wird an document.body gehaengt (fixed), damit es nicht von
// overflow-hidden-Tabellenzellen abgeschnitten wird.
export default class extends Controller {
  static values = { full: String }

  connect() {
    this.element.classList.add("cursor-pointer")
    this.popover = null
    this.hideTimer = null
    this.showTimer = null
    this.boundOnDocClick = this.onDocClick.bind(this)
    this.boundOnKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.removePopover()
  }

  // data-action: mouseenter->truncation-popover#scheduleShow
  scheduleShow() {
    clearTimeout(this.hideTimer)
    if (this.popover) return
    this.showTimer = setTimeout(() => this.show(), 150)
  }

  // data-action: mouseleave->truncation-popover#scheduleHide
  scheduleHide() {
    clearTimeout(this.showTimer)
    this.hideTimer = setTimeout(() => this.removePopover(), 250)
  }

  // data-action: click->truncation-popover#toggle
  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.popover) { this.removePopover() } else { this.show() }
  }

  show() {
    if (this.popover) return

    const pop = document.createElement("div")
    pop.className = "fixed z-50 w-80 max-w-[90vw] rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 p-3"
    pop.addEventListener("mouseenter", () => clearTimeout(this.hideTimer))
    pop.addEventListener("mouseleave", () => this.scheduleHide())

    const text = document.createElement("div")
    text.className = "text-sm text-gray-800 whitespace-pre-wrap break-words max-h-60 overflow-y-auto mb-2"
    text.textContent = this.fullValue

    const footer = document.createElement("div")
    footer.className = "flex justify-end gap-2 border-t border-gray-100 pt-2"

    const copyBtn = document.createElement("button")
    copyBtn.type = "button"
    copyBtn.className = "inline-flex items-center rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
    copyBtn.textContent = "Kopieren"
    copyBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.copyToClipboard(this.fullValue).then(() => {
        copyBtn.textContent = "Kopiert ✓"
        copyBtn.classList.add("text-green-700", "ring-green-300")
        setTimeout(() => {
          copyBtn.textContent = "Kopieren"
          copyBtn.classList.remove("text-green-700", "ring-green-300")
        }, 1500)
      })
    })

    footer.appendChild(copyBtn)
    pop.appendChild(text)
    pop.appendChild(footer)
    document.body.appendChild(pop)

    // Unter dem Element positionieren; wenn unten kein Platz, oberhalb
    const rect = this.element.getBoundingClientRect()
    const popRect = pop.getBoundingClientRect()
    let top = rect.bottom + 6
    if (top + popRect.height > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popRect.height - 6)
    }
    let left = Math.min(rect.left, window.innerWidth - popRect.width - 8)
    pop.style.top = `${top}px`
    pop.style.left = `${Math.max(8, left)}px`

    this.popover = pop
    document.addEventListener("click", this.boundOnDocClick)
    document.addEventListener("keydown", this.boundOnKeydown)
  }

  removePopover() {
    clearTimeout(this.showTimer)
    clearTimeout(this.hideTimer)
    if (this.popover) {
      this.popover.remove()
      this.popover = null
    }
    document.removeEventListener("click", this.boundOnDocClick)
    document.removeEventListener("keydown", this.boundOnKeydown)
  }

  onDocClick(event) {
    if (this.popover && !this.popover.contains(event.target) && !this.element.contains(event.target)) {
      this.removePopover()
    }
  }

  onKeydown(event) {
    if (event.key === "Escape") this.removePopover()
  }

  copyToClipboard(text) {
    // Clipboard-API nur mit aktiver User-Geste (sonst haengt/prompted Chrome) —
    // ohne Geste (z. B. Automation) direkt der execCommand-Fallback.
    const gestureActive = navigator.userActivation ? navigator.userActivation.isActive : true
    if (gestureActive && navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(() => this.copyFallback(text))
    }
    return Promise.resolve(this.copyFallback(text))
  }

  copyFallback(text) {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.style.position = "fixed"
    ta.style.opacity = "0"
    document.body.appendChild(ta)
    ta.select()
    try { document.execCommand("copy") } finally { ta.remove() }
  }
}
